import React from 'react';
import { SceneObject, Evidence } from '../types';
import { isSceneObject, isEvidence } from '../utils/helpers';

interface ModalProps {
  selectedObject: SceneObject | Evidence | null;
  onClose: () => void;
  sceneObjects?: SceneObject[];
  evidenceList?: Evidence[];
  currentCaseId?: string;
}

const Modal: React.FC<ModalProps> = ({ selectedObject, onClose, sceneObjects = [], evidenceList = [], currentCaseId = '' }) => {
  if (!selectedObject) return null;

  // Get location information from the selected object
  const getLocationName = (obj: SceneObject | Evidence) => {
    return obj.location.global?.name || obj.location.local?.name || 'Unknown';
  };

  // Get all people and evidence at the same location and time
  const getLocationData = () => {
    const locationName = getLocationName(selectedObject);
    const objTime = new Date(selectedObject.time);
    
    // Find people at the same location (within 1 hour time window)
    const peopleAtLocation = sceneObjects.filter(obj => {
      if (obj.caseId !== currentCaseId) return false;
      const objLocationName = getLocationName(obj);
      if (objLocationName !== locationName) return false;
      
      const timeDiff = Math.abs(new Date(obj.time).getTime() - objTime.getTime());
      return timeDiff <= 60 * 60 * 1000; // 1 hour window
    });

    // Find evidence at the same location (within 1 hour time window)
    const evidenceAtLocation = evidenceList.filter(obj => {
      if (obj.caseId !== currentCaseId) return false;
      const objLocationName = getLocationName(obj);
      if (objLocationName !== locationName) return false;
      
      const timeDiff = Math.abs(new Date(obj.time).getTime() - objTime.getTime());
      return timeDiff <= 60 * 60 * 1000; // 1 hour window
    });

    return { peopleAtLocation, evidenceAtLocation };
  };

  const { peopleAtLocation, evidenceAtLocation } = getLocationData();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          padding: 20,
          borderRadius: 8,
          maxWidth: 500,
          maxHeight: 400,
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            background: 'none',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer'
          }}
        >
          ×
        </button>

        <div style={{ textAlign: 'left' }}>
          <h2 style={{ marginBottom: 16, textAlign: 'left' }}>{getLocationName(selectedObject)}</h2>
          <p style={{ margin: '8px 0', textAlign: 'left', color: '#666' }}>
            <strong>Time:</strong> {new Date(selectedObject.time).toLocaleString()}
          </p>
          
                     {/* People at this location */}
           {peopleAtLocation.length > 0 && (
             <div style={{ marginTop: 16 }}>
               <h3 style={{ marginBottom: 8, fontSize: '1rem', color: '#333' }}>People Present:</h3>
               <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                 {peopleAtLocation.map(obj => 
                   obj.people && obj.people.map(person => {
                     const displayName = person.replace(/^(Detective|Victim|Person of Interest|Witness|Security Guard)\s+/, '');
                     const timeString = new Date(obj.time).toLocaleTimeString('en-US', {
                       hour: '2-digit',
                       minute: '2-digit',
                       hour12: true
                     });
                     return (
                       <div
                         key={`${person}-${obj.time}`}
                         style={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: '8px',
                           padding: '6px 10px',
                           backgroundColor: '#f8f9fa',
                           borderRadius: '8px',
                           border: '1px solid #e9ecef'
                         }}
                       >
                         <span
                           style={{
                             backgroundColor: '#1976d2',
                             color: 'white',
                             padding: '4px 8px',
                             borderRadius: '12px',
                             fontSize: '0.75rem',
                             fontWeight: 'bold',
                             minWidth: 'fit-content'
                           }}
                         >
                           {displayName}
                         </span>
                         <span
                           style={{
                             color: '#666',
                             fontSize: '0.8rem',
                             fontWeight: '500'
                           }}
                         >
                           {timeString}
                         </span>
                       </div>
                     );
                   })
                 )}
               </div>
             </div>
           )}

          {/* Evidence at this location */}
          {evidenceAtLocation.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 8, fontSize: '1rem', color: '#333' }}>Evidence Found:</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {evidenceAtLocation.map(evidence => (
                  <div key={evidence.id} style={{ 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '6px', 
                    padding: '8px',
                    backgroundColor: '#f9f9f9'
                  }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>{evidence.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}>
                      Type: {evidence.type}
                      {evidence.source && ` • Source: ${evidence.source}`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {peopleAtLocation.length === 0 && evidenceAtLocation.length === 0 && (
            <p style={{ marginTop: 16, color: '#666', fontStyle: 'italic' }}>
              No additional people or evidence found at this location and time.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Modal;
